import { ChartContainer, ChartTooltip } from "@/components/ui/Chart";
import type { ChartConfig } from "@/components/ui/Chart";
import { cn } from "@/lib/utils";
import type { ChartDataItem } from "@/types";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from "recharts";

import {
  SectionBadge,
  formatAxisDate,
  formatCompactCurrency,
  formatCurrency,
  getPointKey,
  getSampledTickIndices,
  type IndexedChartDataItem,
} from "@/components/hlidaci-pes/chart-line-interactive/Shared";
import { StatusPill } from "@/components/portfolio/StatusPill";
import { useLanguage } from "@/context/LanguageContext";
import { useWatchDogContext } from "@/context/WatchDogContext";
import { getWatchDogDateRangeLabel } from "@/components/hlidaci-pes/watchdog-page/WatchDogDateRangeFilter";
import { getPragueIsoWeekKey } from "@/lib/date";
import { Panel } from "@/components/ui/Panel";

type ChartLineGraphPanelProps = {
  config: ChartConfig;
  activeChart: string;
  data: ChartDataItem[];
  pinnedDataPoint: ChartDataItem | null;
  onMouseMove: (state: any) => void;
  onMouseLeave: () => void;
  onDataPointClick: (point: ChartDataItem) => void;
};

export function ChartLineGraphPanel({
  config,
  activeChart,
  data,
  pinnedDataPoint,
  onMouseMove,
  onMouseLeave,
  onDataPointClick,
}: ChartLineGraphPanelProps) {
  const { language } = useLanguage();
  const { dateRange } = useWatchDogContext();
  const copy = {
    cs: {
      chartPoints: "{count} bodů v grafu",
      waitingForData: "Čekáme na data",
      offerDetail: "Detail nabídky",
      date: "Datum",
      price: "Cena",
      legend: "Vysvětlení čar grafu",
      listingPrice: "Cena nabídky",
      weeklyAverage: "Průměr",
      extremeThresholds: "Výrazná odchylka",
      notEnoughData: "Pro vybrané zobrazení zatím nemáme dost dat.",
      tryDifferentMetric: "Zkuste přepnout metriku nebo upravit časové období.",
    },
    en: {
      chartPoints: "{count} chart points",
      waitingForData: "Waiting for data",
      offerDetail: "Offer detail",
      date: "Date",
      price: "Price",
      legend: "Chart line explanation",
      listingPrice: "Listing price",
      weeklyAverage: "Weekly average",
      extremeThresholds: "Far below or above the weekly average",
      notEnoughData: "We do not have enough data for the selected view yet.",
      tryDifferentMetric:
        "Try switching the metric or adjusting the time period.",
    },
  }[language];
  const mainLineColor = "rgb(var(--watchdog-chart-main))";
  const averageLineColor = "rgb(var(--watchdog-chart-average))";
  const chartDataWithIndex: IndexedChartDataItem[] = data.map(
    (item, index) => ({
      ...item,
      chartIndex: index,
    }),
  );
  const weeklyPriceTotals = chartDataWithIndex.reduce((totals, item) => {
    const weekKey = getPragueIsoWeekKey(new Date(item.date));
    const current = totals.get(weekKey) ?? { sum: 0, count: 0 };

    totals.set(weekKey, {
      sum: current.sum + item.price,
      count: current.count + 1,
    });

    return totals;
  }, new Map<string, { sum: number; count: number }>());
  const chartData = chartDataWithIndex.map((item) => {
    const weekTotal = weeklyPriceTotals.get(
      getPragueIsoWeekKey(new Date(item.date)),
    );

    return {
      ...item,
      weeklyAverage: weekTotal ? weekTotal.sum / weekTotal.count : item.price,
      weeklyLowerThreshold: weekTotal
        ? (weekTotal.sum / weekTotal.count) * 0.5
        : item.price * 0.5,
      weeklyUpperThreshold: weekTotal
        ? (weekTotal.sum / weekTotal.count) * 1.5
        : item.price * 1.5,
    };
  });
  const uniqueDateTickIndices = chartDataWithIndex.reduce<number[]>(
    (acc, item, index, items) => {
      if (index === 0 || items[index - 1]?.date !== item.date) {
        acc.push(index);
      }

      return acc;
    },
    [],
  );
  const xAxisTicks = getSampledTickIndices(uniqueDateTickIndices);
  const hasChartData = chartDataWithIndex.length > 0;
  const chartHeightClass = "h-[clamp(220px,34vh,320px)]";
  const activeChartLabel = String(config[activeChart]?.label ?? activeChart);

  return (
    <Panel className="w-full">
      <div className="portfolio-card__header-cols">
        <SectionBadge>{activeChartLabel}</SectionBadge>
        <div className="same-line gap-3">
          <StatusPill className="bg-white">
            {hasChartData
              ? copy.chartPoints.replace("{count}", String(data.length))
              : copy.waitingForData}
          </StatusPill>
          <StatusPill className="bg-white">
            {getWatchDogDateRangeLabel(dateRange, language)}
          </StatusPill>
        </div>
      </div>

      {activeChart === "avgPrice" && hasChartData ? (
        <div
          aria-label={copy.legend}
          className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-medium text-foreground/70"
          role="list"
        >
          <div className="flex items-center gap-2" role="listitem">
            <span
              aria-hidden="true"
              className="app-chart-legend-line app-chart-legend-line--main"
            />
            <span>{copy.listingPrice}</span>
          </div>
          <div className="flex items-center gap-2" role="listitem">
            <span
              aria-hidden="true"
              className="app-chart-legend-line app-chart-legend-line--average"
            />
            <span>{copy.weeklyAverage}</span>
          </div>
          <div className="flex items-center gap-2" role="listitem">
            <span
              aria-hidden="true"
              className="app-chart-legend-line app-chart-legend-line--danger"
            />
            <span>{copy.extremeThresholds}</span>
          </div>
        </div>
      ) : null}

      {hasChartData ? (
        <ChartContainer
          config={config}
          className={cn("aspect-auto w-full", chartHeightClass)}
        >
          <ComposedChart
            data={chartData}
            margin={{ left: 8, right: 8, top: 8, bottom: 0 }}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            onClick={(state: any) => {
              const payload = state?.activePayload?.[0]?.payload;
              if (payload) {
                onDataPointClick(payload as ChartDataItem);
              }
            }}
            className="cursor-pointer"
          >
            <defs>
              <linearGradient
                id="watchdog-chart-fill"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor={mainLineColor}
                  stopOpacity={0.32}
                />
                <stop offset="100%" stopColor={mainLineColor} stopOpacity={0} />
              </linearGradient>
            </defs>

            <CartesianGrid
              vertical={false}
              stroke="currentColor"
              strokeOpacity={0.1}
              strokeDasharray="4 6"
            />
            <XAxis
              dataKey="chartIndex"
              type="number"
              domain={[0, chartDataWithIndex.length - 1]}
              ticks={xAxisTicks}
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={28}
              tickFormatter={(value) =>
                formatAxisDate(chartDataWithIndex[value]?.date ?? "")
              }
            />
            <YAxis
              width={60}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tick={{
                fill: "rgb(var(--softiq-primary) / 0.55)",
                fontSize: 11,
                fontWeight: 500,
              }}
              tickFormatter={(value) => formatCompactCurrency(value)}
            />
            <Area
              type="monotone"
              dataKey="price"
              stroke="none"
              fill="url(#watchdog-chart-fill)"
              fillOpacity={1}
              isAnimationActive={false}
            />
            {activeChart === "avgPrice" ? (
              <>
                <Line
                  dataKey="weeklyLowerThreshold"
                  type="stepAfter"
                  stroke="rgb(var(--softiq-danger))"
                  strokeWidth={1.5}
                  strokeDasharray="3 5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
                <Line
                  dataKey="weeklyUpperThreshold"
                  type="stepAfter"
                  stroke="rgb(var(--softiq-danger))"
                  strokeWidth={1.5}
                  strokeDasharray="3 5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
                <Line
                  dataKey="weeklyAverage"
                  type="stepAfter"
                  stroke={averageLineColor}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
              </>
            ) : null}
            <ChartTooltip
              cursor={{
                stroke: mainLineColor,
                strokeOpacity: 0.18,
                strokeWidth: 1,
                strokeDasharray: "4 6",
              }}
              content={({ payload }) => {
                if (!payload || !payload.length) {
                  return null;
                }

                const dataPoint = payload[0].payload as ChartDataItem;
                const date = new Date(dataPoint.date);

                return (
                  <div className="min-w-[200px] rounded-lg border border-border/70 bg-background p-3 text-sm text-foreground shadow-xl">
                    <div className="space-y-0.5">
                      <p className="card-window__inner-eyebrow text-foreground">
                        {copy.offerDetail}
                      </p>
                      <p className="text-xs font-semibold leading-5 text-foreground">
                        {dataPoint.offer.header}
                      </p>
                    </div>

                    <div className="mt-3 space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-foreground">
                          {copy.date}
                        </span>
                        <span className="text-xs font-medium text-foreground">
                          {date.toLocaleDateString(
                            language === "cs" ? "cs-CZ" : "en-US",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-3">
                        <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-foreground">
                          {copy.price}
                        </span>
                        <span className="text-sm font-semibold text-softiq-secondary">
                          {formatCurrency(dataPoint.price)}
                        </span>
                      </div>

                      <p className="text-[10px] leading-4 text-foreground">
                        {dataPoint.offer.address}
                      </p>
                    </div>
                  </div>
                );
              }}
            />
            <Line
              dataKey="price"
              type="monotone"
              stroke={mainLineColor}
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              activeDot={{
                r: 5,
                fill: "hsl(var(--background))",
                stroke: mainLineColor,
                strokeWidth: 2,
              }}
              dot={(props) => {
                const { cx, cy, payload } = props;
                const point = payload as ChartDataItem;
                const isPinned =
                  getPointKey(pinnedDataPoint) === getPointKey(point);
                const hasCoordinates =
                  typeof cx === "number" && typeof cy === "number";
                const safeX = typeof cx === "number" ? cx : 0;
                const safeY = typeof cy === "number" ? cy : 0;

                return (
                  <circle
                    key={`${point.offer.id}`}
                    cx={safeX}
                    cy={safeY}
                    r={isPinned && hasCoordinates ? 5 : 0}
                    fill="hsl(var(--background))"
                    stroke={mainLineColor}
                    strokeWidth={2}
                    onClick={() => onDataPointClick(point)}
                  />
                );
              }}
            />
          </ComposedChart>
        </ChartContainer>
      ) : (
        <div
          className={cn(
            "flex flex-col items-center justify-center rounded-lg border border-dashed border-border/70 bg-hover/70 px-6 text-center text-foreground",
            chartHeightClass,
          )}
        >
          <p className="app-card-title">{copy.notEnoughData}</p>
          <p className="app-card-description mt-1.5">
            {copy.tryDifferentMetric}
          </p>
        </div>
      )}
    </Panel>
  );
}
